import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import BrandLockup from "@/v2/components/BrandLockup";
import { errText } from "@/v2/lib/hooks";
import "@/v2/v2.css";
import { Eye, EyeOff, Sparkles, ShieldCheck, UserPlus, ArrowLeft } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (f.name.trim().length < 3) return setError("اكتب اسمك الكامل (3 أحرف على الأقل)");
    if (f.password.length < 6) return setError("كلمة المرور يجب أن تكون 6 أحرف على الأقل");
    if (f.password !== f.confirm) return setError("كلمة المرور وتأكيدها غير متطابقين");
    setLoading(true);
    try {
      const res = await register(f.name.trim(), f.password);
      if (res.renamed) toast.info(`اسم الدخول الخاص بك: ${res.login_name}`, { duration: 8000 });
      toast.success("تم إنشاء حسابك — أكمل بياناتك الآن");
      navigate("/onboarding", { replace: true });
    } catch (err) {
      setError(errText(err, "تعذّر إنشاء الحساب، حاول مرة أخرى."));
    } finally { setLoading(false); }
  };

  return (
    <div className="v2-root brand-auth min-h-screen grid lg:grid-cols-2" dir="rtl">
      <div className="brand-auth-story relative hidden lg:flex flex-col justify-between p-12 overflow-hidden v2-grid v2-pattern">
        <div className="absolute top-[-10%] left-[-5%] w-[460px] h-[460px] rounded-full bg-[#91cab8]/15 blur-3xl v2-float" />
        <div className="absolute bottom-[-8%] right-[5%] w-[380px] h-[380px] rounded-full bg-[#bb9661]/12 blur-3xl v2-float" style={{ animationDelay: "2s" }} />
        <div className="relative z-10"><BrandLockup tone="dark" /></div>
        <div className="relative z-10">
          <div className="mb-4 inline-flex"><span className="v2-glass px-4 py-1.5 rounded-full text-sm text-[#91cab8] border-[#91cab8]/30 flex items-center gap-2"><Sparkles size={16} /> انضم إلى رحلة التميّز</span></div>
          <h1 className="text-5xl font-extrabold leading-[1.15] mb-5">حسابك الجديد<br /><span className="v2-gold-text">خطوتين</span> فقط</h1>
          <p className="text-[#bcc8cf] leading-relaxed max-w-md text-lg">اسمك وكلمة المرور فقط، ثم نخصّص لك مسارًا تعليميًا يناسب طبيعة عملك.</p>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-[#bcc8cf] text-sm"><ShieldCheck size={16} className="text-[#91cab8]" /> بيئة آمنة — منصة داخلية للموظفين</div>
      </div>

      <div className="brand-auth-main flex items-center justify-center p-6 sm:p-12">
        <motion.form initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          onSubmit={submit} data-testid="register-form" className="w-full max-w-md">
          <div className="lg:hidden mb-8"><BrandLockup tone="light" /></div>
          <h2 className="text-3xl font-extrabold mb-2">إنشاء حساب جديد</h2>
          <p className="text-[#bcc8cf] mb-8">سجّل باسمك وكلمة المرور، وسنكمل الباقي معك.</p>

          {error && <div data-testid="register-error" className="mb-4 rounded-2xl bg-[#B45309]/15 border border-[#B45309]/40 text-[#F7A08A] px-4 py-3 text-sm">{error}</div>}

          <label className="block text-sm font-semibold mb-2 text-[#bcc8cf]">الاسم الكامل</label>
          <input data-testid="register-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
            placeholder="مثال: أحمد عبدالله المالكي" className="v2-input !h-13 mb-5" />

          <label className="block text-sm font-semibold mb-2 text-[#bcc8cf]">كلمة المرور</label>
          <div className="relative mb-5">
            <input data-testid="register-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })}
              type={show ? "text" : "password"} className="v2-input !pe-12" />
            <button type="button" onClick={() => setShow(!show)} className="absolute inset-y-0 left-3 flex items-center text-[#bcc8cf]">{show ? <EyeOff size={20} /> : <Eye size={20} />}</button>
          </div>

          <label className="block text-sm font-semibold mb-2 text-[#bcc8cf]">تأكيد كلمة المرور</label>
          <input data-testid="register-confirm" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })}
            type={show ? "text" : "password"} className="v2-input mb-7" />

          <button data-testid="register-submit" type="submit" disabled={loading} className="v2-btn w-full">
            <UserPlus size={18} /> {loading ? "جارٍ إنشاء الحساب..." : "إنشاء الحساب"}
          </button>

          <Link to="/login" data-testid="register-to-login"
            className="mt-6 flex items-center justify-center gap-2 text-sm text-[#bcc8cf] hover:text-white">
            لديك حساب؟ تسجيل الدخول <ArrowLeft size={15} />
          </Link>
        </motion.form>
      </div>
    </div>
  );
}
